import { suggestEntityColor, topicColorResolved, type Topic } from "@app/core";
import { Button, Trash2 } from "@app/ui";
import { useState, type ReactNode } from "react";

import { orderByIds } from "../../lib/apply-order.js";
import { Reorder } from "../../lib/motion/index.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore, useThemeStore, useTopicsStore } from "../../stores/index.js";
import { REPARENT_DRAG_THRESHOLD } from "../topics/tree.js";
import { AddEntityRow } from "./AddEntityRow.js";
import { DeleteEntityDialog } from "./DeleteEntityDialog.js";
import { EntityRow } from "./EntityRow.js";
import { ShowInViewsControl } from "./ShowInViewsControl.js";

/** Organize → Topics: the full topic editor. A drag-reorderable tree (roots
 *  follow `topicOrder`, children `topicChildOrder[parentId]` — same persisted
 *  orders the sidebar uses). Every control (color, emoji, Month/Today
 *  visibility, delete) is inline on the row itself — dragging a row's grip
 *  handle horizontally re-parents it, so there's no separate parent picker
 *  and nothing needs to expand on desktop; only the Show-in toggles collapse
 *  behind a chevron on mobile. */
export function TopicsPanel() {
  const { t } = useI18n();
  const topics = useTopicsStore((s) => s.topics);
  const add = useTopicsStore((s) => s.add);
  const rename = useTopicsStore((s) => s.rename);
  const remove = useTopicsStore((s) => s.remove);
  const setParent = useTopicsStore((s) => s.setParent);
  const setIcon = useTopicsStore((s) => s.setIcon);
  const themeConfig = useThemeStore((s) => s.config);
  const setEntityColor = useThemeStore((s) => s.setEntityColor);
  const topicOrder = useSettingsStore((s) => s.topicOrder);
  const setTopicOrder = useSettingsStore((s) => s.setTopicOrder);
  const topicChildOrder = useSettingsStore((s) => s.topicChildOrder);
  const setTopicChildOrder = useSettingsStore((s) => s.setTopicChildOrder);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Topic | null>(null);
  const guard = useDragClickGuard();

  const ids = new Set(topics.map((topic) => topic.id));
  const childrenOf = new Map<string | null, Topic[]>();
  for (const topic of topics) {
    const key = topic.parent_id && ids.has(topic.parent_id) ? topic.parent_id : null;
    childrenOf.set(key, [...(childrenOf.get(key) ?? []), topic]);
  }

  // Drag a topic row far enough right/left to reparent it: right nests it under
  // the immediately preceding sibling (its new last child); left promotes it to
  // its grandparent's level, placed right after its former parent. Plain vertical
  // drags (below the threshold) are left to the per-level Reorder.Group above.
  async function handleHorizontalDrag(topic: Topic, parentId: string | null, orderedLevel: Topic[], offsetX: number) {
    if (offsetX > REPARENT_DRAG_THRESHOLD) {
      const index = orderedLevel.findIndex((candidate) => candidate.id === topic.id);
      if (index <= 0) return;
      const newParent = orderedLevel[index - 1];
      if (!newParent) return;
      await setParent(topic.id, newParent.id);
      const childOrder = topicChildOrder[newParent.id] ?? [];
      await setTopicChildOrder(newParent.id, [...childOrder.filter((id) => id !== topic.id), topic.id]);
    } else if (offsetX < -REPARENT_DRAG_THRESHOLD) {
      if (parentId === null) return;
      const parentTopic = topics.find((candidate) => candidate.id === parentId);
      const grandParentId = parentTopic?.parent_id && ids.has(parentTopic.parent_id) ? parentTopic.parent_id : null;
      await setParent(topic.id, grandParentId);
      const grandLevelOrder = grandParentId === null ? topicOrder : topicChildOrder[grandParentId] ?? [];
      const grandSiblingIds = orderByIds(childrenOf.get(grandParentId) ?? [], grandLevelOrder, (t) => t.id)
        .map((t) => t.id)
        .filter((id) => id !== topic.id);
      const parentIndex = grandSiblingIds.indexOf(parentId);
      const next = [...grandSiblingIds.slice(0, parentIndex + 1), topic.id, ...grandSiblingIds.slice(parentIndex + 1)];
      if (grandParentId === null) await setTopicOrder(next);
      else await setTopicChildOrder(grandParentId, next);
    }
  }

  async function handleAdd(name: string) {
    const usedColors = topics.map((topic, index) => topicColorResolved(themeConfig, topic.id, index));
    const topic = await add(name);
    setEntityColor({ kind: "topic", id: topic.id }, suggestEntityColor(themeConfig, usedColors).hex);
  }

  // One Reorder.Group per tree level; recurses for children (mirrors TopicTree).
  function renderLevel(levelTopics: Topic[], depth: number, parentId: string | null): ReactNode {
    if (levelTopics.length === 0) return null;
    const ordered = orderByIds(levelTopics, parentId === null ? topicOrder : topicChildOrder[parentId] ?? [], (topic) => topic.id);
    return (
      <Reorder.Group
        as="div"
        axis="y"
        values={ordered.map((topic) => topic.id)}
        onReorder={(next) => {
          if (parentId === null) void setTopicOrder(next as string[]);
          else void setTopicChildOrder(parentId, next as string[]);
        }}
      >
        {ordered.map((topic) => (
          <EntityRow
            key={topic.id}
            id={topic.id}
            depth={depth}
            guard={guard}
            swatchHex={topicColorResolved(themeConfig, topic.id, topics.findIndex((x) => x.id === topic.id))}
            colorTarget={{ kind: "entity", target: { kind: "topic", id: topic.id } }}
            colorOverridden={themeConfig.entity.topics?.[topic.id] !== undefined}
            onRevertColor={() => setEntityColor({ kind: "topic", id: topic.id }, null)}
            emoji={topic.icon}
            onEmojiChange={(value) => void setIcon(topic.id, value || null)}
            name={topic.name}
            onRename={(name) => void rename(topic.id, name)}
            expandableActions={<ShowInViewsControl kind="topic" id={topic.id} />}
            deleteAction={
              <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(topic)} title={t("lists.deleteTopicTitle")} aria-label={t("lists.deleteTopicTitle")}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            }
            expanded={expandedId === topic.id}
            onToggleExpand={() => setExpandedId(expandedId === topic.id ? null : topic.id)}
            onHorizontalDragEnd={(offsetX) => void handleHorizontalDrag(topic, parentId, ordered, offsetX)}
          >
            {renderLevel(childrenOf.get(topic.id) ?? [], depth + 1, topic.id)}
          </EntityRow>
        ))}
      </Reorder.Group>
    );
  }

  return (
    <div>
      {renderLevel(childrenOf.get(null) ?? [], 0, null)}
      <AddEntityRow placeholder={t("lists.newTopic")} onAdd={handleAdd} />
      <DeleteEntityDialog
        name={confirming?.name}
        body={t("lists.deleteTopicBody")}
        confirmLabel={t("lists.deleteTopicTitle")}
        onConfirm={() => {
          const topic = confirming;
          setConfirming(null);
          if (topic) void remove(topic.id);
        }}
        onClose={() => setConfirming(null)}
      />
    </div>
  );
}
